import { isSentSouthpawJobStatus } from "@punchup/data/models/messaging";
import dayjs from "@punchup/utils/dayjs";

/**
 * Minimal interface for email job records used by messaging date utilities.
 * Consumers pass their full EmailJobRow objects — only these fields are read.
 */
export interface MessagingJobRecord {
  status: string;
  scheduled_for: string;
}

/**
 * Filter email jobs to only those targeting a specific venue.
 */
export function filterJobsByVenue<
  T extends MessagingJobRecord & { venue_ids: string[] },
>(jobs: T[], venueId: string | undefined): T[] {
  if (!venueId) return [];
  return jobs.filter((job) => job.venue_ids.includes(venueId));
}

/**
 * Count sent and scheduled jobs.
 */
export function getStatusCounts(jobs: MessagingJobRecord[]): {
  sent: number;
  scheduled: number;
} {
  let sent = 0;
  let scheduled = 0;
  for (const job of jobs) {
    const status = job.status.toLowerCase();
    if (isSentSouthpawJobStatus(status)) sent++;
    else if (status === "scheduled") scheduled++;
  }
  return { sent, scheduled };
}

/**
 * Format a date string with hybrid smart formatting:
 * - Relative within 7 days (e.g., "Yesterday", "in 2 days", "3 days ago")
 * - Absolute beyond 7 days (e.g., "Mar 28 at 2:00pm", "Mar 15")
 *
 * Converts to local timezone before computing day diff to avoid UTC off-by-one.
 */
export function formatMessagingDate(
  dateStr: string,
  options?: { includeTime?: boolean }
): string {
  const localTz = dayjs.tz.guess();
  const date = dayjs.utc(dateStr).tz(localTz);
  const now = dayjs.utc().tz(localTz);
  const diffDays = date.startOf("day").diff(now.startOf("day"), "day");

  if (diffDays === 0) {
    return options?.includeTime ? `Today at ${date.format("h:mma")}` : "Today";
  }
  if (diffDays === 1) {
    return options?.includeTime
      ? `Tomorrow at ${date.format("h:mma")}`
      : "Tomorrow";
  }
  if (diffDays === -1) return "Yesterday";
  if (diffDays > 1 && diffDays < 7) {
    return `in ${diffDays} days`;
  }
  if (diffDays < 0 && diffDays > -7) {
    return `${Math.abs(diffDays)} days ago`;
  }
  // Beyond 7 days: absolute format
  if (diffDays >= 7) {
    return date.format("MMM D [at] h:mma");
  }
  return date.format("MMM D");
}

/**
 * Get a single context line for a venue's messaging status.
 * Priority: next scheduled future message > most recent completed message.
 * Returns null if no jobs exist.
 */
export function getContextLine(
  jobs: MessagingJobRecord[]
): { text: string; type: "scheduled" | "sent" } | null {
  if (jobs.length === 0) return null;

  const now = dayjs.utc();

  // Priority: next scheduled future message (D-05)
  const nextScheduled = jobs
    .filter(
      (j) =>
        j.status.toLowerCase() === "scheduled" &&
        dayjs.utc(j.scheduled_for).isAfter(now)
    )
    .sort((a, b) =>
      dayjs.utc(a.scheduled_for).diff(dayjs.utc(b.scheduled_for))
    )[0];

  if (nextScheduled) {
    return {
      text: `Next Scheduled: ${dayjs.utc(nextScheduled.scheduled_for).tz(dayjs.tz.guess()).format("MMM D")}`,
      type: "scheduled",
    };
  }

  // Fallback: most recent completed message
  const lastSent = jobs
    .filter((j) => isSentSouthpawJobStatus(j.status))
    .sort((a, b) =>
      dayjs.utc(b.scheduled_for).diff(dayjs.utc(a.scheduled_for))
    )[0];

  if (lastSent) {
    return {
      text: `Last Sent: ${formatMessagingDate(lastSent.scheduled_for)}`,
      type: "sent",
    };
  }

  return null;
}
